import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Alert,
    FormControlLabel,
    Checkbox,
    CircularProgress
} from '@mui/material';
import { message } from 'antd';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    DragIndicator as DragIcon,
    Folder as FolderIcon,
    BarChart as ChartIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { asyncJobClient } from '../../utils/asyncJobClient';

const MenuManagementPopup = ({ 
    open, 
    onClose, 
    onSave, 
    currentSidebarConfig, 
    visualizationData,
    organizeOrphanedGraphs
}) => {
    const [sidebarConfig, setSidebarConfig] = useState([]);
    const [newTabName, setNewTabName] = useState('');
    const [newSubTabName, setNewSubTabName] = useState('');
    const [selectedParentTab, setSelectedParentTab] = useState('');
    const [selectedParentSubTab, setSelectedParentSubTab] = useState('');
    const [addingMode, setAddingMode] = useState(''); // 'tab' or 'subtab' or 'graph'
    const [includeFilters, setIncludeFilters] = useState(false);
    const [isSavingLayout, setIsSavingLayout] = useState(false);
    const [isSavingChart, setIsSavingChart] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);

    // Initialize with current config or default
    useEffect(() => {
        if (currentSidebarConfig && currentSidebarConfig.length > 0) {
            setSidebarConfig([...currentSidebarConfig]);
        } else {
            // Default sidebar config
            setSidebarConfig([
                { 
                    id: 'overview', 
                    label: 'Overview', 
                    type: 'tab',
                    component: 'OverviewTopSection',
                    order: 0
                },
                { 
                    id: 'customer-locations', 
                    label: 'Customer Locations', 
                    type: 'tab',
                    component: 'OverviewTopPerformers',
                    order: 1
                }
            ]);
        }
        
        // Auto-open save chart form if we have visualization data
        if (visualizationData) {
            setAddingMode('graph');
        }
    }, [currentSidebarConfig, open, visualizationData]);

    // Setup DndKit sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Hierarchy validation functions
    const isValidHierarchy = (draggedItem, targetParent, targetLevel) => {
        const draggedType = draggedItem.type;
        
        // Level 0: Only tabs allowed at root level
        if (targetLevel === 0) {
            return draggedType === 'tab';
        }
        
        // Level 1: Only subtabs allowed as direct children of tabs
        if (targetLevel === 1) {
            return draggedType === 'subtab' && targetParent?.type === 'tab';
        }
        
        // Level 2: Only graphs and components allowed as children of subtabs
        if (targetLevel === 2) {
            return (draggedType === 'graph' || draggedType === 'component') && targetParent?.type === 'subtab';
        }
        
        // No deeper nesting allowed
        return false;
    };

    const canDropInContainer = (draggedItemId, targetContainerId) => {
        const draggedItem = findItemInConfig(draggedItemId);
        if (!draggedItem) return false;

        // Root level (tabs only)
        if (targetContainerId === 'root') {
            return draggedItem.type === 'tab';
        }

        // Find the target container
        const targetContainer = findItemInConfig(targetContainerId);
        if (!targetContainer) return false;

        // Subtabs can only be children of tabs
        if (draggedItem.type === 'subtab') {
            return targetContainer.type === 'tab';
        }

        // Graphs can only be children of subtabs
        if (draggedItem.type === 'graph') {
            return targetContainer.type === 'subtab';
        }

        // Components can only be children of subtabs
        if (draggedItem.type === 'component') {
            return targetContainer.type === 'subtab';
        }

        // Tabs cannot be children of anything except root
        if (draggedItem.type === 'tab') {
            return targetContainerId === 'root';
        }

        return false;
    };

    const findItemInConfig = (itemId, config = sidebarConfig) => {
        for (const tab of config) {
            if (tab.id === itemId) return tab;
            
            if (tab.children) {
                for (const subtab of tab.children) {
                    if (subtab.id === itemId) return subtab;
                    
                    if (subtab.children) {
                        for (const item of subtab.children) {
                            if (item.id === itemId) return item;
                        }
                    }
                }
            }
        }
        return null;
    };

    const getParentTab = (itemId) => {
        for (const tab of sidebarConfig) {
            if (tab.children) {
                for (const subtab of tab.children) {
                    if (subtab.id === itemId) return tab;
                    
                    if (subtab.children) {
                        for (const item of subtab.children) {
                            if (item.id === itemId) return tab;
                        }
                    }
                }
            }
        }
        return null;
    };

    const getParentSubtab = (itemId) => {
        for (const tab of sidebarConfig) {
            if (tab.children) {
                for (const subtab of tab.children) {
                    if (subtab.children) {
                        for (const item of subtab.children) {
                            if (item.id === itemId) return subtab;
                        }
                    }
                }
            }
        }
        return null;
    };

    const validateMove = (draggedItemId, targetContainerId) => {
        const draggedItem = findItemInConfig(draggedItemId);
        const targetContainer = targetContainerId === 'root' ? null : findItemInConfig(targetContainerId);
        
        if (!canDropInContainer(draggedItemId, targetContainerId)) {
            const draggedType = draggedItem?.type || 'unknown';
            const targetType = targetContainer?.type || 'root';
            
            console.warn(`Invalid hierarchy: Cannot place ${draggedType} under ${targetType}`);
            return false;
        }
        
        return true;
    };

    // Apply slot item mapping to update the sidebar configuration
    const applySlotItemMapping = (config, slotItemMapping) => {
        console.log('Applying slot item mapping:', slotItemMapping);
        
        const newConfig = [];
        const itemMap = new Map();
        
        // First, create a flat map of all items for easy lookup
        const createItemMap = (items, parentId = null) => {
            items.forEach(item => {
                itemMap.set(item.id, { ...item, parentId });
                if (item.children) {
                    createItemMap(item.children, item.id);
                }
            });
        };
        
        createItemMap(config);
        console.log("newConfig: ", newConfig, itemMap)
        // Process the mapping to build new structure
        
        // First, identify all root-level slots (tabs)
        // Look for slots that contain tab-type items
        const rootSlots = [];
        Object.entries(slotItemMapping.asObject).forEach(([slotId, itemId]) => {
            const item = itemMap.get(itemId);
            if (item && item.type === 'tab') {
                rootSlots.push(slotId);
            }
        });
        
        console.log('Root slots found:', rootSlots);
        
        rootSlots.forEach(slotId => {
            const itemId = slotItemMapping.asObject[slotId];
            const item = itemMap.get(itemId);
            
            if (!item) return;
            
            // Create new tab
            const newTab = {
                ...item,
                children: []
            };
            
            // Find all children of this tab (looking for slots that start with itemId, not slotId)
            const childSlots = Object.keys(slotItemMapping.asObject).filter(slot => {
                // console.log('parts: ', parts)
                // Check if this slot represents a direct child of this tab
                return slot.startsWith(itemId)
            });
            console.log('childSlots for', itemId, ':', childSlots)
            
            childSlots.forEach(childSlotId => {
                const childItemId = slotItemMapping.asObject[childSlotId];
                const childItem = itemMap.get(childItemId);
                
                if (!childItem) return;
                
                // Create new subtab
                const newSubTab = {
                    ...childItem,
                    children: []
                };
                
                // Find all grandchildren of this subtab (using childItemId, not childSlotId)
                const grandChildSlots = Object.keys(slotItemMapping.asObject).filter(slot => 
                    slot.startsWith(`${childItemId}-`)
                );
                
                grandChildSlots.forEach(grandChildSlotId => {
                    const grandChildItemId = slotItemMapping.asObject[grandChildSlotId];
                    const grandChildItem = itemMap.get(grandChildItemId);
                    
                    if (grandChildItem) {
                        newSubTab.children.push({ ...grandChildItem });
                    }
                });
                
                // Sort grandchildren by slot order
                newSubTab.children.sort((a, b) => {
                    const aSlot = Object.keys(slotItemMapping.asObject).find(slot => 
                        slotItemMapping.asObject[slot] === a.id && slot.startsWith(`${childItemId}-`)
                    );
                    const bSlot = Object.keys(slotItemMapping.asObject).find(slot => 
                        slotItemMapping.asObject[slot] === b.id && slot.startsWith(`${childItemId}-`)
                    );
                    return (aSlot || '').localeCompare(bSlot || '');
                });
                
                newTab.children.push(newSubTab);
            });
            
            // Sort children by slot order
            newTab.children.sort((a, b) => {
                const aSlot = Object.keys(slotItemMapping.asObject).find(slot => 
                    slotItemMapping.asObject[slot] === a.id && slot.startsWith(`${itemId}-`)
                );
                const bSlot = Object.keys(slotItemMapping.asObject).find(slot => 
                    slotItemMapping.asObject[slot] === b.id && slot.startsWith(`${itemId}-`)
                );
                return (aSlot || '').localeCompare(bSlot || '');
            });
            
            // Set isSection if has children
            if (newTab.children.length > 0) {
                newTab.isSection = true;
            }
            
            newConfig.push(newTab);
        });
        
        // Sort tabs by their position in the root mapping
        newConfig.sort((a, b) => {
            const aSlot = Object.keys(slotItemMapping.asObject).find(slot => 
                slotItemMapping.asObject[slot] === a.id && !slot.includes('-')
            );
            const bSlot = Object.keys(slotItemMapping.asObject).find(slot => 
                slotItemMapping.asObject[slot] === b.id && !slot.includes('-')
            );
            return (aSlot || '').localeCompare(bSlot || '');
        });
        
        // Update orders
        newConfig.forEach((tab, tabIndex) => {
            tab.order = tabIndex;
            if (tab.children) {
                tab.children.forEach((subtab, subtabIndex) => {
                    subtab.order = subtabIndex;
                    if (subtab.children) {
                        subtab.children.forEach((item, itemIndex) => {
                            item.order = itemIndex;
                        });
                    }
                });
            }
        });
        
        console.log('New config after mapping:', newConfig);
        return newConfig;
    };

    // Helper function to move items in config
    const moveItemInConfig = (config, activeId, overId) => {
        const newConfig = JSON.parse(JSON.stringify(config)); // Deep clone
        
        const draggedItem = findItemInConfig(activeId, config);
        const targetItem = findItemInConfig(overId, config);
        
        if (!draggedItem || !targetItem) {
            return config;
        }

        // Remove dragged item from its current location
        const removeFromConfig = (items, itemId) => {
            for (let i = 0; i < items.length; i++) {
                if (items[i].id === itemId) {
                    items.splice(i, 1);
                    return;
                }
                if (items[i].children) {
                    removeFromConfig(items[i].children, itemId);
                }
            }
        };

        // Add item to new location
        const addToConfig = (items, draggedItem, targetItem, targetType) => {
            if (targetType === 'tab' && draggedItem.type === 'tab') {
                // Tab to tab - reorder at root level
                const targetIndex = items.findIndex(item => item.id === targetItem.id);
                items.splice(targetIndex, 0, draggedItem);
            } else if (targetType === 'subtab' && draggedItem.type === 'subtab') {
                // Subtab to subtab - move to same parent tab
                for (const tab of items) {
                    if (tab.children && tab.children.find(child => child.id === targetItem.id)) {
                        const targetIndex = tab.children.findIndex(child => child.id === targetItem.id);
                        tab.children.splice(targetIndex, 0, draggedItem);
                        break;
                    }
                }
            } else if ((targetType === 'graph' || targetType === 'component') && 
                      (draggedItem.type === 'graph' || draggedItem.type === 'component')) {
                // Graph to graph - move to same parent subtab
                for (const tab of items) {
                    if (tab.children) {
                        for (const subtab of tab.children) {
                            if (subtab.children && subtab.children.find(child => child.id === targetItem.id)) {
                                const targetIndex = subtab.children.findIndex(child => child.id === targetItem.id);
                                subtab.children.splice(targetIndex, 0, draggedItem);
                                return;
                            }
                        }
                    }
                }
            } else if (targetType === 'tab' && draggedItem.type === 'subtab') {
                // Subtab to different tab
                const targetTab = items.find(item => item.id === targetItem.id);
                if (targetTab) {
                    if (!targetTab.children) targetTab.children = [];
                    if (!targetTab.isSection) targetTab.isSection = true;
                    targetTab.children.push(draggedItem);
                }
            } else if (targetType === 'subtab' && (draggedItem.type === 'graph' || draggedItem.type === 'component')) {
                // Graph to different subtab
                for (const tab of items) {
                    if (tab.children) {
                        const targetSubtab = tab.children.find(child => child.id === targetItem.id);
                        if (targetSubtab) {
                            if (!targetSubtab.children) targetSubtab.children = [];
                            targetSubtab.children.push(draggedItem);
                            return;
                        }
                    }
                }
            }
        };

        removeFromConfig(newConfig, activeId);
        addToConfig(newConfig, draggedItem, targetItem, targetItem.type);

        // Update orders
        newConfig.forEach((tab, tabIndex) => {
            tab.order = tabIndex;
            if (tab.children) {
                tab.children.forEach((subtab, subtabIndex) => {
                    subtab.order = subtabIndex;
                    if (subtab.children) {
                        subtab.children.forEach((item, itemIndex) => {
                            item.order = itemIndex;
                        });
                    }
                });
            }
        });

        return newConfig;
    };

    // DndKit drag handlers
    const handleDragStart = useCallback((event) => {
        console.log('Drag start:', event);
        setActiveId(event.active.id);
        const item = findItemInConfig(event.active.id);
        setDraggedItem(item);
    }, [sidebarConfig]);

    const handleDragEnd = useCallback((event) => {
        console.log('Drag end:', event);
        const { active, over } = event;
        
        setActiveId(null);
        setDraggedItem(null);
        
        if (!over || active.id === over.id) {
            return;
        }

        // Get the dragged item and target item
        const draggedItem = findItemInConfig(active.id);
        const targetItem = findItemInConfig(over.id);
        
        if (!draggedItem || !targetItem) {
            console.warn('Could not find dragged or target item');
            return;
        }

        // Validate the move based on item types and hierarchy
        const isDraggedTab = draggedItem.type === 'tab';
        const isDraggedSubtab = draggedItem.type === 'subtab';
        const isDraggedGraph = draggedItem.type === 'graph' || draggedItem.type === 'component';
        
        const isTargetTab = targetItem.type === 'tab';
        const isTargetSubtab = targetItem.type === 'subtab';
        const isTargetGraph = targetItem.type === 'graph' || targetItem.type === 'component';

        // Check if this is a valid move according to hierarchy rules
        let isValidMove = false;
        let moveDescription = '';
        
        if (isDraggedTab && isTargetTab) {
            // Tab to tab - valid reordering at root level
            isValidMove = true;
            moveDescription = 'reordering tabs';
        } else if (isDraggedSubtab && isTargetTab) {
            // Subtab to tab - move subtab to different tab
            isValidMove = true;
            moveDescription = 'moving subtab to different tab';
        } else if (isDraggedSubtab && isTargetSubtab) {
            // Subtab to subtab - can move between any tabs
            isValidMove = true;
            moveDescription = 'reordering subtabs';
        } else if (isDraggedGraph && isTargetSubtab) {
            // Graph to subtab - move graph to different subtab
            isValidMove = true;
            moveDescription = 'moving graph to different subtab';
        } else if (isDraggedGraph && isTargetGraph) {
            // Graph to graph - can move between any subtabs
            isValidMove = true;
            moveDescription = 'reordering graphs';
        } else {
            // Invalid moves: tab as child, graph to tab, etc.
            isValidMove = false;
        }
        
        if (!isValidMove) {
            message.warning({
                content: 'Invalid move: Must follow hierarchy (tab > subtab > graph). Tabs cannot be children, graphs cannot go directly to tabs.',
                duration: 5
            });
            return;
        }

        // Apply the move
        const newConfig = moveItemInConfig(sidebarConfig, active.id, over.id);
        setSidebarConfig(newConfig);
        
    }, [sidebarConfig]);

    const handleDragOver = useCallback((event) => {
        // Handle drag over for visual feedback
        console.log('Drag over:', event);
    }, []);

    const handleAddTab = () => {
        if (!newTabName.trim()) return;
        
        const newTab = {
            id: `custom-${Date.now()}`,
            label: newTabName.trim(),
            type: 'tab',
            isCustom: true,
            children: [],
            order: sidebarConfig.length
        };
        
        setSidebarConfig(prev => [...prev, newTab]);
        setNewTabName('');
        setAddingMode('');
    };

    const handleAddSubTab = () => {
        if (!newSubTabName.trim() || !selectedParentTab) return;
        
        // Validate hierarchy: subtabs can only be added to tabs
        const parentTab = sidebarConfig.find(tab => tab.id === selectedParentTab);
        if (!parentTab || parentTab.type !== 'tab') {
            message.error('Subtabs can only be added to tabs');
            return;
        }
        
        const newSubTab = {
            id: `custom-subtab-${Date.now()}`,
            label: newSubTabName.trim(),
            type: 'subtab',
            isCustom: true,
            order: 0
        };
        
        setSidebarConfig(prev => prev.map(item => {
            if (item.id === selectedParentTab) {
                // Convert to section if it's not already
                if (!item.isSection) {
                    return {
                        ...item,
                        isSection: true,
                        children: [newSubTab]
                    };
                } else {
                    const children = [...(item.children || []), newSubTab];
                    // Update orders for children
                    children.forEach((child, index) => {
                        child.order = index;
                    });
                    return {
                        ...item,
                        children
                    };
                }
            }
            return item;
        }));
        
        setNewSubTabName('');
        setSelectedParentTab('');
        setAddingMode('');
    };

    const handleAddGraph = async () => {
        if (!selectedParentSubTab || !visualizationData) return;
        const messageKey = 'filter-generation-progress';

        setIsSavingChart(true);
        
        try {
            // Validate hierarchy: graphs can only be added to subtabs
            const parentTab = sidebarConfig.find(tab => tab.id === selectedParentTab);
            const parentSubTab = parentTab?.children?.find(subtab => subtab.id === selectedParentSubTab);
            
            if (!parentSubTab || parentSubTab.type !== 'subtab') {
                message.error('Graphs can only be added to subtabs');
                return;
            }

            // Prepare visualization data with filter information if filters are enabled
            let finalVisualizationData = { ...visualizationData };
            
            if (includeFilters && visualizationData.sql && visualizationData.workflow) {
                try {
                    console.log('Generating filter configuration for chart...');
                    
                    // Make request to generate-filtered-query endpoint to analyze available filters
                    // message.loading('Generating filter configuration...', 0, messageKey);
                    
                    const response = await asyncJobClient.generateFilteredQuery(
                        visualizationData.workflow,
                        visualizationData.sql,
                        {
                            onProgress: (progress) => {
                                message.loading({
                                    content: `Generating filters... ${progress.progress}% - ${progress.stage}`,
                                    key: messageKey,
                                    duration: 0
                                });
                            }
                        }
                    );
                    
                    message.destroy(messageKey); // Clear loading message
                    
                    if (response.success) {
                        const {originalSql, filteredSql, location, date_start, date_end, unavailableFilters, workflow} = response;
                        // Store comprehensive filter configuration with the visualization data
                        finalVisualizationData.filterConfig = {
                            originalSql,
                            filteredSql,
                            location,
                            date_start,
                            date_end,
                            unavailableFilters,
                            workflow,
                        };

                        message.success('Chart saved with filter configuration.');
                    } else {
                        throw new Error(response.error || response.message || 'Failed to analyze filter capabilities');
                    }
                } catch (error) {
                    message.destroy(messageKey); // Clear loading message
                    console.error('Error analyzing filter capabilities:', error);
                    message.warning('Chart saved but filter analysis encountered an issue. Filters may not work properly.');
                    
                    // Store basic filter configuration as fallback
                    finalVisualizationData.filterConfig = {
                        enabled: false,
                        workflow: visualizationData.workflow,
                        originalSql: visualizationData.sql,
                        error: error.message
                    };
                }
            } else {
                message.success('Chart saved successfully.');
            }
        
            const graphItem = {
                id: `graph-${Date.now()}`,
                label: visualizationData.title || 'Saved Chart',
                type: 'graph',
                isCustom: true,
                visualizationData: finalVisualizationData,
                includeFilters: includeFilters,
                parentTab: selectedParentTab,
                parentSubTab: selectedParentSubTab,
                order: 0
            };
            
            setSidebarConfig(prev => prev.map(tab => {
                if (tab.id === selectedParentTab) {
                    return {
                        ...tab,
                        children: tab.children?.map(subTab => {
                            if (subTab.id === selectedParentSubTab) {
                                const children = [...(subTab.children || []), graphItem];
                                children.forEach((child, index) => {
                                    child.order = index;
                                });
                                return {
                                    ...subTab,
                                    children
                                };
                            }
                            return subTab;
                        })
                    };
                }
                return tab;
            }));
            
            setSelectedParentTab('');
            setSelectedParentSubTab('');
            setAddingMode('');
        } catch (error) {
            console.error('Error adding graph:', error);
            message.error('Failed to save chart. Please try again.');
        } finally {
            setIsSavingChart(false);
        }
    };

    const handleRemoveItem = (itemId, parentId = null) => {
        console.log('handleRemoveItem called with:', { itemId, parentId });
        
        setSidebarConfig(prev => {
            console.log('Current config before removal:', JSON.stringify(prev, null, 2));
            
            if (!parentId) {
                // Remove top-level item (tab)
                const filtered = prev.filter(item => item.id !== itemId);
                filtered.forEach((item, index) => {
                    item.order = index;
                });
                return filtered;
            }

            // Remove nested item (subtab, graph, or component)
            const newConfig = prev.map(tab => {
                // Check if the parent is a direct child of this tab (subtab removal)
                if (tab.children && tab.children.some(child => child.id === itemId)) {
                    console.log('Removing subtab with ID:', itemId);
                    return {
                        ...tab,
                        children: tab.children.filter(child => child.id !== itemId).map((child, index) => ({
                            ...child,
                            order: index
                        }))
                    };
                }

                // Check if we need to remove from a subtab's children (graph/component removal)
                if (tab.children) {
                    const hasSubtabWithTargetParent = tab.children.some(subtab => subtab.id === parentId);
                    
                    if (hasSubtabWithTargetParent) {
                        console.log('Found parent subtab:', parentId, 'removing child:', itemId);
                        
                        const updatedChildren = tab.children.map(subtab => {
                            if (subtab.id === parentId && subtab.children) {
                                console.log('Before filtering subtab children:', subtab.children.map(c => c.id));
                                const filteredChildren = subtab.children.filter(item => item.id !== itemId);
                                console.log('After filtering subtab children:', filteredChildren.map(c => c.id));
                                
                                return {
                                    ...subtab,
                                    children: filteredChildren.map((item, index) => ({
                                        ...item,
                                        order: index
                                    }))
                                };
                            }
                            return subtab;
                        });

                        return {
                            ...tab,
                            children: updatedChildren
                        };
                    }
                }

                return tab;
            });
            
            console.log('New config after removal:', JSON.stringify(newConfig, null, 2));
            return newConfig;
        });
    };

    const saveLayoutToAPI = async () => {
        setIsSavingLayout(true);
        try {
            // Organize orphaned graphs before saving
            const organizedConfig = organizeOrphanedGraphs(sidebarConfig);
            
            const layoutData = {
                sidebarConfig: organizedConfig,
                timestamp: new Date().toISOString(),
                version: '1.0'
            };

            const response = await axios.post('/workflow/save-layout', layoutData, {
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('auth-token')
                }
            });

            if (response.data.success) {
                console.log('Layout saved successfully:', response.data);
                message.success('Layout saved successfully!');
            } else {
                throw new Error(response.data.error || 'Failed to save layout');
            }
        } catch (error) {
            console.error('Error saving layout:', error);
            message.error('Failed to save layout: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsSavingLayout(false);
        }
    };

    const handleSave = () => {
        // Organize orphaned graphs before saving
        const organizedConfig = organizeOrphanedGraphs(sidebarConfig);
        setSidebarConfig(organizedConfig);
        
        // Save layout to API first, then close
        saveLayoutToAPI().then(() => {
            onSave(organizedConfig);
            onClose();
        });
    };

    const getAvailableParentTabs = () => {
        // Only tabs can be parents for subtabs
        return sidebarConfig.filter(item => item.type === 'tab');
    };

    const getAvailableSubTabs = (parentTabId) => {
        const parentTab = sidebarConfig.find(tab => tab.id === parentTabId);
        if (parentTab && parentTab.children) {
            // Only subtabs can be parents for graphs
            return parentTab.children.filter(child => child.type === 'subtab');
        }
        return [];
    };

    // Sortable Item Component
    const SortableItem = ({ item, parentId = null }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({
            id: item.id,
        });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
            marginBottom: '8px',
        };

        const isCustom = item.isCustom;
        
        return (
            <div ref={setNodeRef} style={style} {...attributes}>
                <ListItem
                    sx={{
                        pl: parentId ? 4 : 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: parentId ? '#f8f9fa' : '#ffffff',
                        cursor: 'grab',
                        '&:active': {
                            cursor: 'grabbing'
                        },
                        '&:hover': {
                            backgroundColor: parentId ? '#e9ecef' : '#f8f9fa'
                        }
                    }}
                >
                    <div {...listeners}>
                        <DragIcon sx={{ mr: 1, color: '#757575' }} />
                    </div>
                    
                    {item.type === 'graph' ? (
                        <ChartIcon sx={{ mr: 1, color: '#1976d2' }} />
                    ) : item.isSection ? (
                        <FolderIcon sx={{ mr: 1, color: '#757575' }} />
                    ) : null}
                    
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                    {item.label}
                                </Typography>
                                {isCustom && (
                                    <Chip 
                                        label="Custom" 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined" 
                                    />
                                )}
                                <Chip 
                                    label={item.type} 
                                    size="small" 
                                    color={
                                        item.type === 'tab' ? 'success' : 
                                        item.type === 'subtab' ? 'warning' : 
                                        'info'
                                    }
                                    variant="outlined" 
                                />
                            </Box>
                        }
                        secondary={
                            item.type === 'graph' ? 'Chart visualization' : 
                            item.type === 'component' ? `Component: ${item.component}` :
                            item.type === 'subtab' ? 'Can contain graphs and components' :
                            item.type === 'tab' ? 'Can contain subtabs' : 
                            item.type
                        }
                    />
                    
                    <ListItemSecondaryAction>
                        {isCustom && (
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveItem(item.id, parentId)}
                                sx={{ color: '#d32f2f' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </ListItemSecondaryAction>
                </ListItem>
            </div>
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '95vh' }
            }}
        >
            <DialogTitle>
                <Typography variant="h6">
                    Manage Sidebar Menu
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Add tabs, subtabs, and save charts. Drag items to reorder them.
                </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ pb: 1 }}>
                {visualizationData && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        You're saving: <strong>{visualizationData.title}</strong>
                    </Alert>
                )}

                {/* Add buttons */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAddingMode('tab')}
                    >
                        Add Tab
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAddingMode('subtab')}
                    >
                        Add Subtab
                    </Button>
                    {visualizationData && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ChartIcon />}
                            onClick={() => setAddingMode('graph')}
                            color="primary"
                        >
                            Save Chart
                        </Button>
                    )}
                </Box>

                {/* Add forms */}
                {addingMode === 'tab' && (
                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Tab Name"
                            value={newTabName}
                            onChange={(e) => setNewTabName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="contained" size="small" onClick={handleAddTab}>
                                Add Tab
                            </Button>
                            <Button variant="text" size="small" onClick={() => setAddingMode('')}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                )}

                {addingMode === 'subtab' && (
                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Parent Tab</InputLabel>
                            <Select
                                value={selectedParentTab}
                                onChange={(e) => setSelectedParentTab(e.target.value)}
                            >
                                {getAvailableParentTabs().map(tab => (
                                    <MenuItem key={tab.id} value={tab.id}>
                                        {tab.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            size="small"
                            label="Subtab Name"
                            value={newSubTabName}
                            onChange={(e) => setNewSubTabName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="contained" size="small" onClick={handleAddSubTab}>
                                Add Subtab
                            </Button>
                            <Button variant="text" size="small" onClick={() => setAddingMode('')}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                )}

                {addingMode === 'graph' && (
                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Select Tab</InputLabel>
                            <Select
                                value={selectedParentTab}
                                onChange={(e) => {
                                    setSelectedParentTab(e.target.value);
                                    setSelectedParentSubTab(''); // Reset subtab when tab changes
                                }}
                            >
                                {getAvailableParentTabs().map(tab => (
                                    <MenuItem key={tab.id} value={tab.id}>
                                        {tab.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        {selectedParentTab && (
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Select Subtab</InputLabel>
                                <Select
                                    value={selectedParentSubTab}
                                    onChange={(e) => setSelectedParentSubTab(e.target.value)}
                                >
                                    {getAvailableSubTabs(selectedParentTab).map(subTab => (
                                        <MenuItem key={subTab.id} value={subTab.id}>
                                            {subTab.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeFilters}
                                    onChange={(e) => setIncludeFilters(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Add filters"
                            sx={{ mb: 2, display: 'block' }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            When enabled, the chart will be affected by filter selections on the dashboard
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                                variant="contained" 
                                size="small" 
                                onClick={handleAddGraph}
                                disabled={!selectedParentSubTab || isSavingChart}
                                startIcon={isSavingChart ? <CircularProgress size={16} /> : <SaveIcon />}
                            >
                                {isSavingChart ? 'Saving...' : 'Save Chart'}
                            </Button>
                            <Button 
                                variant="text" 
                                size="small" 
                                onClick={() => setAddingMode('')}
                                disabled={isSavingChart}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Current sidebar structure with DndKit */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Current Sidebar Structure
                    <Typography variant="caption" display="block" color="text.secondary">
                        Drag items to reorder them
                    </Typography>
                </Typography>
                
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                >
                    <div style={{ 
                        // maxHeight: '300px', 
                        // overflow: 'auto',
                        padding: '8px'
                    }}>
                        <SortableContext
                            items={(() => {
                                const allIds = [];
                                const collectIds = (items) => {
                                    items.forEach(item => {
                                        allIds.push(item.id);
                                        if (item.children) {
                                            collectIds(item.children);
                                        }
                                    });
                                };
                                collectIds(sidebarConfig);
                                return allIds;
                            })()}
                            strategy={verticalListSortingStrategy}
                        >
                            {sidebarConfig
                                .sort((a, b) => (a.order || 0) - (b.order || 0))
                                .map(item => (
                                <div key={item.id}>
                                    <SortableItem item={item} />
                                    {item.children && item.children
                                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                                        .map(child => (
                                        <div key={child.id} style={{ marginLeft: '16px' }}>
                                            <SortableItem item={child} parentId={item.id} />
                                            {child.children && child.children
                                                .sort((a, b) => (a.order || 0) - (b.order || 0))
                                                .map(grandChild => (
                                                    <div key={grandChild.id} style={{ marginLeft: '32px' }}>
                                                        <SortableItem item={grandChild} parentId={child.id} />
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </SortableContext>
                    </div>
                    
                    <DragOverlay>
                        {activeId && draggedItem ? (
                            <SortableItem item={draggedItem} />
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave}>
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MenuManagementPopup;